import {
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { createServerFn } from '@tanstack/react-start'
import { BlogListingHeader, BlogPostList } from '@/features/blog/components'
import { generateLinks, generateMetaTags, routeSEO } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'

// NOTE: keep this as `const x: any = createServerFn(...).handler(...)` — NOT
// `const x = createServerFn(...).handler(...) as any`. TanStack Start's
// compiler only extracts a server function into a server-only chunk when the
// `.handler(...)` call is the *direct, unwrapped* initializer of the const
// (see @tanstack/start-plugin-core's isTopLevelDirectCallCandidate, which
// checks `parent.init === node`). Wrapping the whole expression in
// `as any` makes the parent a TSAsExpression instead of the VariableDeclarator,
// so the compiler silently skips extraction — this file's D1 access
// (including the `cloudflare:workers` import) then ships straight into the
// client bundle and throws in the browser instead of running on the server.
const fetchPublishedPosts: any = createServerFn({
  method: 'GET',
}).handler(async (ctx: any) => {
  const { getD1 } =
    await import('@/server/infrastructure/database/d1-connection')
  const { D1BlogPostRepository } =
    await import('@/server/infrastructure/persistence/repositories/D1BlogPostRepository')
  const { ListBlogPostsUseCase } =
    await import('@/server/application/blog/use-cases/ListBlogPostsUseCase')
  const { toPublicBlogPostOutput } =
    await import('@/server/application/blog/dto/BlogPostDto')
  const db = getD1()
  const repository = new D1BlogPostRepository(db)
  const useCase = new ListBlogPostsUseCase(repository)
  const result = await useCase.execute({
    page: ctx.data.page,
    limit: 12,
    status: 'published',
  })
  if (!result.success || !result.data) {
    return {
      data: [],
      total: 0,
      page: 1,
      totalPages: 1,
    }
  }

  // Transform to public DTO (excludes author info) and match PaginatedResponse interface
  const totalPages = Math.ceil(result.data.total / result.data.limit)
  return {
    data: result.data.posts.map((post) => toPublicBlogPostOutput(post as any)),
    total: result.data.total,
    page: result.data.page,
    totalPages,
  }
})

export const Route = createFileRoute('/blogs/')({
  validateSearch: (search: Record<string, unknown>) => {
    const requestedPage = Number(search.page)

    return {
      page:
        Number.isSafeInteger(requestedPage) && requestedPage > 0
          ? requestedPage
          : 1,
    }
  },
  search: {
    middlewares: [stripSearchParams({ page: 1 })],
  },
  loaderDeps: ({ search: { page } }: any) => ({
    page,
  }),
  loader: (({ deps }: any) =>
    fetchPublishedPosts({ data: { page: deps.page } })) as any,
  head: ({ match }) => {
    const canonical =
      match.search.page > 1 ? `/blogs?page=${match.search.page}` : '/blogs'
    const seo = { ...routeSEO['/blogs'], canonical }

    return {
      meta: generateMetaTags(seo),
      links: generateLinks(seo),
    }
  },
  component: BlogListingPage,
})

function BlogListingPage() {
  const loaderData = Route.useLoaderData()
  const navigate = useNavigate()
  const search = Route.useSearch()

  const posts = loaderData?.data ?? []
  const total = loaderData?.total ?? 0
  const totalPages = loaderData?.totalPages ?? 1

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: renderJsonLd(
            getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Blog', url: '/blogs' },
            ]),
          ),
        }}
      />

      <BlogListingHeader />

      <div className="mt-10">
        <BlogPostList
          posts={posts}
          total={total}
          page={search.page}
          totalPages={totalPages}
          onPageChange={(p: number) => navigate({ search: { page: p } } as any)}
        />
      </div>
    </>
  )
}
